import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useActiveContextReady } from './useActiveContextReady';
import { config } from '../config';

export interface ConfigSnapshotListItem {
  id: string;
  deviceId: string;
  format: string;
  checksum: string;
  sizeBytes: number;
  source: string;
  configChangeLogId: string | null;
  createdAt: string;
}

export interface ConfigSnapshotDetail {
  id: string;
  deviceId: string;
  configText: string;
  format: string;
  checksum: string;
  sizeBytes: number;
  source: string;
  configChangeLogId: string | null;
  createdAt: string;
}

/** Whether the device's vendor profile can restore a stored config. */
export interface RestoreSupport {
  supported: boolean;
  /** Seconds the device may be unreachable while it restarts to take a config. */
  restartWaitSecs: number | null;
}

interface ConfigSnapshotListResponse {
  snapshots: ConfigSnapshotListItem[];
  restore: RestoreSupport;
}

/**
 * The list endpoint's whole response, cached under `['configSnapshots', …]`.
 * Every reader of that key goes through here and narrows with `select`, so the
 * cached shape is the same for all of them.
 */
function useConfigSnapshotList<T>(
  deviceId: string | undefined,
  source: string | undefined,
  select: (r: ConfigSnapshotListResponse) => T,
) {
  const contextReady = useActiveContextReady();
  return useQuery<ConfigSnapshotListResponse, Error, T>({
    queryKey: ['configSnapshots', deviceId, source],
    queryFn: async () => {
      if (!deviceId) throw new Error('Device ID is required');
      const params = source ? `?source=${encodeURIComponent(source)}` : '';
      const response = await axios.get<ConfigSnapshotListResponse>(
        `${config.backendApi}/api/devices/${deviceId}/config-snapshots${params}`,
        { withCredentials: true }
      );
      return response.data;
    },
    select,
    enabled: !!deviceId && contextReady,
  });
}

export function useConfigSnapshots(deviceId: string | undefined, source?: string) {
  return useConfigSnapshotList(deviceId, source, (r) => r.snapshots);
}

export function useConfigRestoreSupport(deviceId: string | undefined) {
  return useConfigSnapshotList(deviceId, undefined, (r) => r.restore);
}

export function useConfigSnapshotDetail(deviceId: string | undefined, snapshotId: string | undefined) {
  const contextReady = useActiveContextReady();
  return useQuery<ConfigSnapshotDetail>({
    queryKey: ['configSnapshot', deviceId, snapshotId],
    queryFn: async () => {
      if (!deviceId || !snapshotId) throw new Error('Device ID and Snapshot ID are required');
      const response = await axios.get<ConfigSnapshotDetail>(
        `${config.backendApi}/api/devices/${deviceId}/config-snapshots/${snapshotId}`,
        { withCredentials: true }
      );
      return response.data;
    },
    enabled: !!deviceId && !!snapshotId && contextReady,
  });
}

export interface CaptureConfigResponse {
  success: boolean;
  error: string | null;
  devicesChecked: number;
  snapshotsCaptured: number;
  devicesUnchanged: number;
  errors: string[];
}

export function useCaptureDeviceConfig(deviceId: string) {
  const queryClient = useQueryClient();
  return useMutation<CaptureConfigResponse>({
    mutationFn: async () => {
      const response = await axios.post<CaptureConfigResponse>(
        `${config.backendApi}/api/devices/${deviceId}/capture-config`,
        {},
        { withCredentials: true }
      );
      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['configSnapshots', deviceId] });
    },
  });
}

/**
 * `restored_with_differences`: the restore ran, and the snapshot taken after it
 * still differs from the one restored; its detail says where.
 */
export type ConfigRestoreStatus =
  | 'queued'
  | 'running'
  | 'succeeded'
  | 'restored_with_differences'
  | 'failed'
  | 'unknown';

export interface ConfigRestore {
  id: string;
  deviceId: string;
  snapshotId: string | null;
  /** What the device had when it was restored, captured just before. */
  replacedSnapshotId: string | null;
  requestedBy: string | null;
  status: ConfigRestoreStatus;
  detail: string | null;
  createdAt: string;
  startedAt: string | null;
  finishedAt: string | null;
  /** The change whose rollback asked for this restore. Absent from older backends. */
  changeManagementId?: string | null;
}

export function isRestoreInProgress(r: Pick<ConfigRestore, 'status'>): boolean {
  return r.status === 'queued' || r.status === 'running';
}

/**
 * Recent restores of a device. Re-read every few seconds while one is in
 * progress, besides the websocket event: the device being restored may be the
 * one this browser reaches the internet through.
 */
export function useConfigRestores(deviceId: string | undefined) {
  const contextReady = useActiveContextReady();
  return useQuery<ConfigRestore[]>({
    queryKey: ['configRestores', deviceId],
    queryFn: async () => {
      if (!deviceId) throw new Error('Device ID is required');
      const response = await axios.get<{ restores: ConfigRestore[] }>(
        `${config.backendApi}/api/devices/${deviceId}/config-restores`,
        { withCredentials: true }
      );
      return response.data.restores;
    },
    enabled: !!deviceId && contextReady,
    refetchInterval: (query) =>
      query.state.data?.some(isRestoreInProgress) ? 5000 : false,
  });
}

export interface RestorePreview {
  /** `null` with no error: the device already has this config. */
  diff: string | null;
  error: string | null;
  /** What to know before restoring, one per line. Absent from older backends. */
  warnings?: string | null;
}

export function usePreviewRestore(deviceId: string) {
  return useMutation<RestorePreview, Error, string>({
    mutationFn: async (snapshotId) => {
      const response = await axios.post<RestorePreview>(
        `${config.backendApi}/api/devices/${deviceId}/config-snapshots/${snapshotId}/restore/preview`,
        {},
        { withCredentials: true }
      );
      return response.data;
    },
  });
}

export function useRestoreSnapshot(deviceId: string) {
  const queryClient = useQueryClient();
  return useMutation<ConfigRestore, Error, string>({
    mutationFn: async (snapshotId) => {
      const response = await axios.post<ConfigRestore>(
        `${config.backendApi}/api/devices/${deviceId}/config-snapshots/${snapshotId}/restore`,
        {},
        { withCredentials: true }
      );
      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['configRestores', deviceId] });
    },
  });
}
